// src/services/professionalTax.service.js
// Professional Tax Service - Frontend API Integration

import { api } from './api';
import { getProfessionalTax as getFallbackPT } from '../config/payrollConfig';

/**
 * Professional Tax Service
 * Fetches PT calculations from backend API with fallback to frontend calculation
 */
class ProfessionalTaxService {
    /**
     * Calculate Professional Tax for an employee
     * Uses backend API with fallback to frontend calculation
     *
     * @param {string} employeeId - Employee UUID
     * @param {number} grossSalary - Monthly gross salary
     * @param {string} month - Month in YYYY-MM format (e.g., '2025-01')
     * @param {string} state - Employee state (optional, backend will fetch if not provided)
     * @returns {Promise<Object>} PT calculation result
     */
    async calculatePT(employeeId, grossSalary, month, state = null) {
        try {
            // Call backend API for PT calculation
            // FIX: Include state parameter - use company/org state for PT calculation
            const payload = {
                employeeId,
                grossSalary,
                month
            };
            // Only add state if provided (company state overrides employee state lookup)
            if (state) {
                payload.state = state;
            }
            const response = await api.post('/api/professional-tax/calculate', payload);

            if (response.data.success) {
                const result = response.data.data;
                return {
                    ptAmount: result.ptAmount || 0,
                    slabDetails: result.slabDetails,
                    exemptionApplied: result.exemptionApplied || false,
                    cappingApplied: result.cappingApplied || false,
                    metadata: result.metadata,
                    source: 'backend'
                };
            } else {
                throw new Error('Backend PT calculation failed');
            }
        } catch (error) {
            console.warn('⚠️ Backend PT calculation failed, using frontend fallback:', error.message);

            // Fallback to frontend calculation
            const ptAmount = getFallbackPT(state, grossSalary);
            return {
                ptAmount,
                slabDetails: null,
                exemptionApplied: false,
                cappingApplied: false,
                metadata: {
                    fallback: true,
                    state,
                    grossSalary,
                    reason: 'Backend API unavailable'
                },
                source: 'frontend-fallback'
            };
        }
    }

    /**
     * Get PT slabs for a specific state
     *
     * @param {string} stateCode - State code (e.g., 'MAHARASHTRA', 'KARNATAKA')
     * @returns {Promise<Array>} PT slabs for the state
     */
    async getPTSlabsByState(stateCode) {
        try {
            const response = await api.get(`/api/professional-tax/states/${stateCode}/slabs`);

            if (response.data.success) {
                return response.data.data.slabs || [];
            }
            return [];
        } catch (error) {
            console.error('Error fetching PT slabs:', error);
            return [];
        }
    }

    /**
     * Get all states with PT configuration
     *
     * @returns {Promise<Array>} List of states with PT
     */
    async getAllPTStates() {
        try {
            const response = await api.get('/api/professional-tax/states');

            if (response.data.success) {
                return response.data.data.states || [];
            }
            return [];
        } catch (error) {
            console.error('Error fetching PT states:', error);
            return [];
        }
    }

    /**
     * Get PT exemptions for an employee
     *
     * @param {string} employeeId - Employee UUID
     * @returns {Promise<Array>} List of PT exemptions
     */
    async getEmployeePTExemptions(employeeId) {
        try {
            const response = await api.get(`/api/professional-tax/exemptions/employee/${employeeId}`);

            if (response.data.success) {
                return response.data.data.exemptions || [];
            }
            return [];
        } catch (error) {
            console.error('Error fetching PT exemptions:', error);
            return [];
        }
    }

    /**
     * Create PT exemption for an employee
     *
     * @param {Object} exemptionData - Exemption details
     * @returns {Promise<Object>} Created exemption
     */
    async createPTExemption(exemptionData) {
        try {
            const response = await api.post('/api/professional-tax/exemptions', exemptionData);
            return response.data;
        } catch (error) {
            console.error('Error creating PT exemption:', error);
            throw error;
        }
    }

    /**
     * Get PT deduction history for an employee
     *
     * @param {string} employeeId - Employee UUID
     * @param {number} financialYear - Financial year (optional)
     * @returns {Promise<Array>} PT deduction history
     */
    async getEmployeePTHistory(employeeId, financialYear = null) {
        try {
            const params = financialYear ? { financialYear } : {};
            const response = await api.get(`/api/professional-tax/history/employee/${employeeId}`, { params });

            if (response.data.success) {
                return response.data.data.history || [];
            }
            return [];
        } catch (error) {
            console.error('Error fetching PT history:', error);
            return [];
        }
    }

    /**
     * Synchronous PT calculation using frontend slabs only
     * Use this when you need immediate PT calculation without async/await
     *
     * @param {string} state - Employee state
     * @param {number} grossSalary - Monthly gross salary
     * @returns {number} PT amount
     */
    calculatePTSync(state, grossSalary) {
        return getFallbackPT(state, grossSalary);
    }
}

export default new ProfessionalTaxService();
